/**
 * SectionInspector - the contextual right panel for the selected section. Shows
 * its role (switchable), a token-insert palette, the type-specific binding/config
 * (reusing the existing per-type editor), and the tokens it uses (as chips, with
 * an unbound state for orphaned references). The canvas stays the place prose is
 * typed; the inspector is where a section is bound and classified.
 */

import { Pill, SegmentedControl, TokenChip, inferTokenSource } from '@protoss/ui';
import { type DocHeading, type PreviewSample } from '../../../../components/doc';
import { humanizeToken } from '../../../../components/doc/previewSample';
import { SowSectionConfigEditor } from '../sow-template/SowSectionConfigEditor';
import {
  CONTENT_TYPE_META,
  SELECTABLE_CONTENT_TYPES,
  SHORT_CONTENT_TYPE_LABEL,
  sectionLabel,
  type LogicalContentType,
  type LogicalSection,
} from '../../lib/sowContract';
import type { CatalogToken } from '../../lib/sowTemplateApi';
import { isTokenBound } from '../../lib/sowTokenValidity';
import { TokenPalettePopover } from './TokenPalettePopover';

const TOKEN_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

interface UsedToken {
  path: string;
  label: string;
  source: ReturnType<typeof inferTokenSource>;
  unbound: boolean;
}

/** Tokens referenced in the section (excluding per-row table scope), with bound state. */
function sectionTokens(section: LogicalSection, catalogKeys: Set<string>): UsedToken[] {
  const json = JSON.stringify(section.heading ? [section.heading, ...section.body] : section.body);
  const seen = new Set<string>();
  const out: UsedToken[] = [];
  for (const m of json.matchAll(TOKEN_RE)) {
    const path = (m[1] ?? '').replace(/^protoss:/, '');
    if (!path || path.startsWith('item.') || seen.has(path)) continue;
    seen.add(path);
    out.push({
      path,
      label: humanizeToken(path),
      source: inferTokenSource(path),
      unbound: !isTokenBound(path, catalogKeys),
    });
  }
  return out;
}

export interface SectionInspectorProps {
  section: LogicalSection;
  canEdit: boolean;
  sample: PreviewSample;
  headings: DocHeading[];
  catalogTokens: CatalogToken[];
  paletteDisabled: boolean;
  onSwitchType: (id: string, type: LogicalContentType) => void;
  onChangeSection: (next: LogicalSection) => void;
  onSampleChange: (next: PreviewSample) => void;
  onInsertToken: (token: CatalogToken) => void;
}

export function SectionInspector({
  section,
  canEdit,
  sample,
  headings,
  catalogTokens,
  paletteDisabled,
  onSwitchType,
  onChangeSection,
  onSampleChange,
  onInsertToken,
}: SectionInspectorProps) {
  const meta = CONTENT_TYPE_META[section.contentType];
  const isSelectable = SELECTABLE_CONTENT_TYPES.includes(section.contentType);
  const catalogKeys = new Set(catalogTokens.map((t) => t.token));
  const tokens = sectionTokens(section, catalogKeys);

  return (
    <div className="sow-studio-inspector__body">
      <div className="sow-studio-inspector__title">
        <span className="sow-studio-inspector__eyebrow">Section</span>
        <span className="sow-studio-inspector__heading">{sectionLabel(section)}</span>
      </div>

      <div className="sow-studio-inspector__group">
        <span className="sow-studio-inspector__group-label">Role</span>
        {canEdit ? (
          <SegmentedControl<LogicalContentType>
            ariaLabel="Section role"
            value={isSelectable ? section.contentType : ('static' as LogicalContentType)}
            onChange={(type) => onSwitchType(section.id, type)}
            options={SELECTABLE_CONTENT_TYPES.map((type) => ({
              value: type,
              label: SHORT_CONTENT_TYPE_LABEL[type],
            }))}
          />
        ) : (
          <Pill variant={meta.tone}>{meta.label}</Pill>
        )}
        {!isSelectable ? <p className="sow-studio-inspector__hint">{meta.description}</p> : null}
      </div>

      {canEdit ? (
        <div className="sow-studio-inspector__group">
          <TokenPalettePopover
            tokens={catalogTokens}
            disabled={paletteDisabled}
            onInsert={onInsertToken}
          />
        </div>
      ) : null}

      <div className="sow-studio-inspector__group">
        <SowSectionConfigEditor
          section={section}
          isEditing={canEdit}
          sample={sample}
          headings={headings}
          onChange={onChangeSection}
          onSampleChange={onSampleChange}
        />
      </div>

      {tokens.length > 0 ? (
        <div className="sow-studio-inspector__group">
          <span className="sow-studio-inspector__group-label">Tokens in use</span>
          <div className="sow-studio-inspector__tokens">
            {tokens.map((t) => (
              <TokenChip key={t.path} label={t.label} source={t.source} unbound={t.unbound} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
